/**
 * ============================================================================
 * METRICS SELECTORS — home-page card views over getMetrics + the catalog.
 * ============================================================================
 *
 * GENERIC LOGIC — no metric names are hardcoded here. Each metric's `type`
 * comes from the getMetricDefinitions catalog and decides the aggregation:
 *   percentage → 100 × SUM(numeratorValue) / SUM(denominatorValue)
 *   volume     → SUM(value), with attainment when the catalog sets baseMetricKey
 *   count      → SUM(value), no attainment (informational)
 *
 * Home page renders:
 *   Top section:    all 'percentage' metrics
 *   Bottom section: all 'volume' + 'count' metrics
 */

import { DcrRecord, GetMetricsOutput, MetricDefinition } from 'src/types/metricsApi.types';
import { MetricGroupSelection, metricGroupMatches } from 'src/features/api/filterPredicate';
import { OTD_CARRIER_KEYS } from 'src/features/api/tableSelectors';
import {
  MetricCatalog,
  accumulateMetric,
  buildMetricCatalog,
  finalizeRate,
  normalizeMetricType,
  parseReportingPeriod,
} from 'src/features/api/aggregationCore';

// ──── Output types ────

export interface PercentageMetricData {
  metricName: string;
  owner: string;
  goal: number;
  /** null = the metric had no observations that week (NOT the same as 0%). */
  weeklyValues: Array<number | null>;
  weeks: string[];
}

export interface VolumeMetricData {
  metricName: string;
  owner: string;
  goal: number;
  /** Empty string = no baseline (render as raw count like Demand). */
  baselineMetricName: string;
  /** null = no observations that week (NOT the same as a real 0). */
  weeklyVolumes: Array<number | null>;
  /** Empty array = no attainment (no baseline); null = no data that week. */
  weeklyAttainment: Array<number | null>;
  weeks: string[];
}

export interface WbrDashboardData {
  weeks: string[];
  percentageMetrics: PercentageMetricData[];
  volumeMetrics: VolumeMetricData[];
}

// ──── Main selector ────

export function selectWbrDashboardData(
  response: GetMetricsOutput | undefined,
  definitions: MetricDefinition[] | undefined,
  dcrPredicate?: (dcr: DcrRecord) => boolean,
  /** Metric Group chips — scope the volume cards only (see below). */
  metricGroups?: MetricGroupSelection,
): WbrDashboardData | null {
  if (!response || response.data.length === 0 || !definitions?.length) return null;

  // Home cards show history + current only — FUTURE periods from the shared
  // window are excluded (spec default-metrics-window R4).
  const nonForward = response.data.filter((period) => period.timeline !== 'FUTURE');
  if (nonForward.length === 0) return null;

  // Row-level filters (QuickSight parity): constrain DCRs before aggregation.
  const periods = dcrPredicate
    ? nonForward.map((period) => ({ ...period, dcrData: period.dcrData.filter(dcrPredicate) }))
    : nonForward;
  const weeks = periods.map((period) => parseReportingPeriod(period.reportingPeriod).label);

  const catalog: MetricCatalog = buildMetricCatalog(definitions);

  // Only metrics that actually appear in the data become cards.
  const present = new Set<string>();
  for (const period of periods) {
    for (const dcr of period.dcrData) {
      for (const entry of dcr.metrics) present.add(entry.metricKey);
    }
  }
  // Metric Group scopes the VOLUME section only. The Delivery Performance
  // (percentage) cards mirror the dashboard's On Time Delivery % visual, which
  // has fixed rows and ignores the control.
  const ordered = definitions
    .filter((definition) => present.has(definition.metricKey))
    // Per-DCR OTD rates feed the derived %DCRs rows on the detail page; they
    // are never their own card and must never be averaged.
    .filter((definition) => !OTD_CARRIER_KEYS.has(definition.metricKey))
    .filter(
      (definition) =>
        !metricGroups ||
        normalizeMetricType(definition.type) === 'percentage' ||
        metricGroupMatches(definition.metricGroup, metricGroups),
    )
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const percentageMetrics: PercentageMetricData[] = [];
  const volumeMetrics: VolumeMetricData[] = [];

  for (const definition of ordered) {
    const owner = definition.owner ?? '';
    const goal = parseFloat(definition.goal ?? '0') || 0;

    if (normalizeMetricType(definition.type) === 'percentage') {
      const weeklyValues = periods.map((period) => {
        const { sumNum, sumDen, seen } = accumulateMetric(period.dcrData, definition.metricKey, catalog);
        // No carrier in this week's slice → no data. Reporting 0% would read as
        // "we delivered nothing", which is a different claim.
        return seen ? finalizeRate(sumNum, sumDen) : null;
      });
      percentageMetrics.push({ metricName: definition.name, owner, goal, weeklyValues, weeks });
      continue;
    }

    // volume or count
    const weeklyVolumes = periods.map((period) => {
      const { sum, seen } = accumulateMetric(period.dcrData, definition.metricKey, catalog);
      return seen ? sum : null;
    });
    const baseline = definition.baseMetricKey
      ? catalog.get(definition.baseMetricKey)
      : undefined;
    let weeklyAttainment: Array<number | null> = [];
    if (baseline) {
      const weeklyBaseline = periods.map((period) => {
        const { sum, seen } = accumulateMetric(period.dcrData, baseline.metricKey, catalog);
        return seen ? sum : null;
      });
      weeklyAttainment = weeklyVolumes.map((volume, i) => {
        const base = weeklyBaseline[i];
        return volume === null || base === null ? null : finalizeRate(volume, base);
      });
    }

    volumeMetrics.push({
      metricName: definition.name,
      owner,
      goal,
      baselineMetricName: baseline?.name ?? '',
      weeklyVolumes,
      weeklyAttainment,
      weeks,
    });
  }

  return { weeks, percentageMetrics, volumeMetrics };
}
